import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getFirestore,
  collection,
  onSnapshot,
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { getAuth, signOut } from 'firebase/auth';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { app } from './firebase-config';
import { recalculateUserPoints } from './recalculatePoints';
import { SmartImage, shrinkImage, uploadThumbnails } from './imageUtils';

const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

const MinProfil = () => {
  const [userName, setUserName] = useState('');
  const [userPoints, setUserPoints] = useState(0);
  const [hostPoints, setHostPoints] = useState(0);
  const [hostedEventsCount, setHostedEventsCount] = useState(0);
  const [participatedEventsCount, setParticipatedEventsCount] = useState(0);
  const [totalEventsCount, setTotalEventsCount] = useState(0);
  const [events, setEvents] = useState([]);
  const [eventTypeSummary, setEventTypeSummary] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [showHostedEvents, setShowHostedEvents] = useState(false);
  const [showPointsTable, setShowPointsTable] = useState(false);
  const [profileImage, setProfileImage] = useState('');

  const navigate = useNavigate();

  // -----------------------------
  // 🔐 USER AUTH AND REALTIME DATA
  // -----------------------------
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const userDocRef = doc(db, 'users', currentUser.uid);
    const unsubscribeUser = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setUserName(data.name);
        setUserPoints(data.points || 0);
        setHostPoints(data.host_points || 0);
        if (data.profileImageUrl) setProfileImage(data.profileImageUrl);
      }
    });

    return () => unsubscribeUser();
  }, [navigate]);

  // -----------------------------
  // 🕒 REALTIME EVENTS LISTENER
  // -----------------------------
  useEffect(() => {
    if (!userName) return;

    const eventsCollection = collection(db, 'events');
    const unsubscribeEvents = onSnapshot(eventsCollection, (snapshot) => {
      let eventsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      eventsList = eventsList.map((event) => {
        const [year, quarter] = event.Kvartal.split(' ');
        const quarterNumber = parseInt(quarter[1], 10);
        event.parsedKvartal = new Date(year, (quarterNumber - 1) * 3);
        return event;
      });
      eventsList.sort((a, b) => b.parsedKvartal - a.parsedKvartal);

      setEvents(eventsList);
      setTotalEventsCount(eventsList.length);

      const hosted = eventsList.filter(
        (e) => Array.isArray(e.Vert) && e.Vert.includes(userName)
      );
      setHostedEventsCount(hosted.length);

      const hostedPoints = hosted.reduce((sum, e) => {
        if (e.event_type === 'Vino🍷' || e.event_type === 'KL🥩') return sum + 4;
        if (e.event_type === 'Blåtur🌍') return sum + 7;
        if (e.event_type === 'KD🧠') return sum + 5;
        return sum;
      }, 0);
      setHostPoints(hostedPoints);

      const hostedEventDetails = hosted.map(
        (e) => `${e.sted_konsept}, ${e.event_type}, ${e.Kvartal}`
      );
      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      updateDoc(userDocRef, {
        host_points: hostedPoints,
        hosted_events: hostedEventDetails
      });
    });

    return () => unsubscribeEvents();
  }, [userName]);

// -----------------------------
// 📊 RECALCULATE PARTICIPATION SUMMARY
// -----------------------------
useEffect(() => {
  if (!userName || events.length === 0) return;

  const attendedEvents = events.filter((e) =>
    Array.isArray(e.Deltagelse) && e.Deltagelse.includes(userName)
  );

  const summary = {};
  attendedEvents.forEach((e) => {
    const type = e.event_type;
    if (!summary[type]) summary[type] = { count: 0, points: 0 };
    summary[type].count += 1;

    if (type === 'Blåtur🌍') summary[type].points += 7;
    else if (type === 'KD🧠') summary[type].points += 5;
    else if (type === 'KL🥩' || type === 'Vino🍷') summary[type].points += 4;
  });

  setEventTypeSummary(summary);
  setParticipatedEventsCount(attendedEvents.length);
}, [events, userName]);


  // -----------------------------
  // 📸 PROFILE IMAGE UPLOAD HANDLER
  // -----------------------------
  const handleProfileUpload = async (e) => {
    const original = e.target.files[0];
    if (!original) return;
    const user = auth.currentUser;
    if (!user) return;
    const file = await shrinkImage(original, 1024);

    const fileRef = ref(storage, `profile_pictures/${user.uid}/${file.name}`);
    const uploadTask = uploadBytesResumable(fileRef, file);

    uploadTask.on(
      'state_changed',
      null,
      (error) => console.error('Upload error:', error),
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        await uploadThumbnails(storage, fileRef.fullPath, file, [200]);
        await updateDoc(doc(db, 'users', user.uid), { profileImageUrl: url });
        setProfileImage(url);
      }
    );
  };

  // -----------------------------
  // ✅ CHECKBOX HANDLER (unchanged)
  // -----------------------------
  const handleCheckboxChange = async (eventId, isChecked) => {
    try {
      const eventRef = doc(db, 'events', eventId);
      const event = events.find((e) => e.id === eventId);
      if (!event) return;

      const pointsMap = { 'Vino🍷': 4, 'KL🥩': 4, 'Blåtur🌍': 7, 'KD🧠': 5 };
      const pointsToAdd = pointsMap[event.event_type] || 0;
      const attendedEventDetail = `${event.sted_konsept}, ${event.event_type}, ${event.Kvartal}`;
      const userRef = doc(db, 'users', auth.currentUser.uid);

      if (isChecked) {
        await updateDoc(eventRef, { Deltagelse: arrayUnion(userName) });
        await updateDoc(userRef, {
          points: userPoints + pointsToAdd,
          attended_events: arrayUnion(attendedEventDetail)
        });
      } else {
        await updateDoc(eventRef, { Deltagelse: arrayRemove(userName) });
        await updateDoc(userRef, {
          points: userPoints - pointsToAdd,
          attended_events: arrayRemove(attendedEventDetail)
        });
      }
    } catch (error) {
      console.error('Error updating participation: ', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('userName');
      navigate('/login');
    } catch (error) {
      console.error('Error logging out: ', error);
    }
  };

  const toggleRow = (id) =>
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const individualPointsMap = {
    'Vino🍷': 4,
    'KL🥩': 4,
    'Blåtur🌍': 7,
    'KD🧠': 5
  };

  // -----------------------------
  // 🖥️ RENDER
  // -----------------------------
  return (
    <div>
      <div className="minProfil_text">
        <button className="button-17" onClick={handleLogout}>
          Logg ut
        </button>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px' }}>
          <h1>Profil: {userName}</h1>
          {profileImage ? (
            <SmartImage
              src={profileImage}
              size={200}
              alt="Profilbilde"
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <div
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#999',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '12px',
              }}
            >
              Ingen bilde
            </div>
          )}
          <label className="button-18 file-input-label">
            Velg bilde
            <input type="file" accept="image/png, image/jpeg" onChange={handleProfileUpload} hidden />
          </label>
        </div>

        {userName === 'Matias' && (
          <button className="button-17" onClick={recalculateUserPoints}>Recalculate Points</button>
        )}

        <h3>Arrangement:</h3>
        <div>Antall events arrangert: {hostedEventsCount}</div>
        <div>Poeng for arrangement: {hostPoints}</div>
        <button
          className="button-17"
          onClick={() => setShowHostedEvents((prev) => !prev)}
        >
          {showHostedEvents
            ? 'Skjul mine arrangerte events'
            : 'Vis mine arrangerte events'}
        </button>

{showHostedEvents && (
  <div className="table-container">
    <table className="hosted-events-table">
      <thead>
        <tr>
          <th>Sted/ konsept</th>
          <th>Type</th>
          <th>Kvartal</th>
          <th>Vertpoeng</th>
        </tr>
      </thead>
      <tbody>
        {events
          .filter(
            (event) =>
              Array.isArray(event.Vert) && event.Vert.includes(userName)
          )
          .map((event) => (
            <tr key={event.id}>
              <td>{event.sted_konsept}</td>
              <td>{event.event_type}</td>
              <td>{event.Kvartal}</td>
              <td>{individualPointsMap[event.event_type] || 0}</td>
            </tr>
          ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan="3" style={{ fontWeight: 'bold' }}>Totalt</td>
          <td style={{ fontWeight: 'bold' }}>{hostPoints}</td>
        </tr>
      </tfoot>
    </table>
  </div>
)}


    <br />
    <br />
        <h3>Deltakelse:</h3>
        <div>Antall events deltatt: {participatedEventsCount}</div>
        <div>Poeng for deltagelse: {userPoints}</div>

        <button
          className="button-17"
          onClick={() => setShowPointsTable((prev) => !prev)}
        >
          {showPointsTable ? 'Skjul poengberegning' : 'Vis poengberegning'}
        </button>

        {showPointsTable && (
          <div className="table-container">
          <table className="points-summary-table">
            <thead>
              <tr>
                <th>Event type</th>
                <th>Poeng per event</th>
                <th>Antall</th>
                <th>Deltakerpoeng</th>
              </tr>
            </thead>
            <tbody>
              {['Vino🍷', 'KL🥩', 'KD🧠', 'Blåtur🌍'].map((type) => (
                <tr key={type}>
                  <td>{type}</td>
                  <td>{individualPointsMap[type]}</td>
                  <td>{eventTypeSummary[type]?.count || 0}</td>
                  <td>{eventTypeSummary[type]?.points || 0}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 'bold' }}>
                <td>Totalt</td>
                <td>-</td>
                <td>
                  {Object.values(eventTypeSummary).reduce(
                    (sum, t) => sum + (t?.count || 0),
                    0
                  )}
                </td>
                <td>
                  {Object.values(eventTypeSummary).reduce(
                    (sum, t) => sum + (t?.points || 0),
                    0
                  )}
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        )}

        <br />
        <h5>Deltakelseregister:</h5>
        <div className="totalEventsCount">
          Totalt antall events: {totalEventsCount}
        </div>
      </div>
      
      <div className="table-container">
      <table>
        <thead>
          <tr>
            <th>Sted/konsept</th>
            <th>Type</th>
            <th>Kvartal</th>
            <th>Deltatt?</th>
            <th>Poeng</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <React.Fragment key={event.id}>
              <tr
                className={
                  !event.Deltagelse.includes(userName) ? 'not-registered' : ''
                }
              >
                <td>{event.sted_konsept}</td>
                <td>{event.event_type}</td>
                <td>{event.Kvartal}</td>
                <td>
                  Deltatt?{' '}
                  <input
                    className="checkbox"
                    type="checkbox"
                    checked={event.Deltagelse.includes(userName)}
                    onChange={(e) =>
                      handleCheckboxChange(event.id, e.target.checked)
                    }
                  />
                  <button className="button-18" onClick={() => toggleRow(event.id)}>
                    {expandedRows[event.id] ? 'Vis mindre' : 'Vis mer'}
                  </button>
                </td>
                <td>
                  {individualPointsMap[event.event_type] || 0}
                </td>
              </tr>
              {expandedRows[event.id] && (
                <tr>
                  <td colSpan="5">
                    <div className="minProfil_text">
                      <ul>
                        Vert:{' '}
                        {Array.isArray(event.Vert)
                          ? event.Vert.join(', ')
                          : ''}
                        .
                        <br />
                        <div className="deltagelse_underline">Deltagelse:</div>
                        {event.Deltagelse.map((p, i) => (
                          <p key={i}>{p}</p>
                        ))}
                      </ul>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default MinProfil;
